// InventoryImportDialog.jsx — import inventory from a spreadsheet (CSV/XLSX) or
// by scanning a receiving document (AI itemize + categorize). Preview-then-commit.
import React, { useState, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, Box,
  ToggleButton, ToggleButtonGroup, Typography, Alert, LinearProgress, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, Select, MenuItem, TextField, IconButton,
  FormControlLabel, Checkbox,
} from "@mui/material";
import { UploadFileRounded, DocumentScannerRounded, DeleteOutline } from "@mui/icons-material";
import {
  parseInventoryFile, scanReceivingDoc, commitInventoryImport, INVENTORY_CATEGORIES,
} from "../../utils/inventoryImportApi";

const catLabel = (v) => (INVENTORY_CATEGORIES.find((c) => c.value === v) || {}).label || v;

export default function InventoryImportDialog({ open, onClose, onImported }) {
  const [mode, setMode] = useState("spreadsheet"); // spreadsheet | scan
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState(null); // null until parsed
  const [aiConsent, setAiConsent] = useState(false); // opt-in for external AI scan
  const fileRef = useRef(null);

  const reset = () => { setItems(null); setError(null); setBusy(false); setAiConsent(false); };
  const close = () => { reset(); onClose && onClose(); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setError(null); setItems(null);
    try {
      const data = mode === "scan" ? await scanReceivingDoc(file) : await parseInventoryFile(file);
      setItems((data?.items || []).map((it, i) => ({ ...it, _id: i })));
      if (!data?.items?.length) setError("No items found in that file.");
    } catch (err) {
      setError(err?.response?.data?.message || "Import failed.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setField = (id, field, val) =>
    setItems((arr) => arr.map((it) => (it._id === id ? { ...it, [field]: val } : it)));
  const removeRow = (id) => setItems((arr) => arr.filter((it) => it._id !== id));

  const commit = async () => {
    setBusy(true); setError(null);
    try {
      const res = await commitInventoryImport(items.map(({ _id, _row, ...rest }) => rest));
      onImported && onImported(res?.inserted || 0);
      close();
    } catch (err) {
      setError(err?.response?.data?.message || "Commit failed.");
      setBusy(false);
    }
  };

  const counts = (items || []).reduce((a, it) => ((a[it.category] = (a[it.category] || 0) + 1), a), {});
  const accept = mode === "scan" ? ".pdf,.png,.jpg,.jpeg,.webp" : ".csv,.xlsx,.xls";

  return (
    <Dialog open={open} onClose={close} maxWidth="lg" fullWidth>
      <DialogTitle>Import Inventory</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <ToggleButtonGroup
            exclusive size="small" value={mode}
            onChange={(_, v) => { if (v) { setMode(v); reset(); } }}
          >
            <ToggleButton value="spreadsheet"><UploadFileRounded sx={{ mr: 1 }} /> Spreadsheet (CSV / Excel)</ToggleButton>
            <ToggleButton value="scan"><DocumentScannerRounded sx={{ mr: 1 }} /> Scan receiving doc (AI)</ToggleButton>
          </ToggleButtonGroup>

          {mode === "scan" && (
            <Alert severity="warning" sx={{ alignItems: "flex-start" }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                External AI processing — opt-in required
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Scanning sends this document to an external AI provider (Anthropic) to read and
                itemize it. The document leaves SFM and is processed off-site. Do not scan documents
                you are not authorized to share externally. The Spreadsheet option keeps all data in-house.
              </Typography>
              <FormControlLabel
                control={<Checkbox size="small" checked={aiConsent} onChange={(e) => setAiConsent(e.target.checked)} />}
                label="I am authorized to send this document to an external AI provider for processing."
              />
            </Alert>
          )}

          <Box>
            <input ref={fileRef} type="file" accept={accept} hidden onChange={onFile} id="inv-import-file"
              disabled={busy || (mode === "scan" && !aiConsent)} />
            <label htmlFor="inv-import-file">
              <Button variant="outlined" component="span" disabled={busy || (mode === "scan" && !aiConsent)}
                startIcon={mode === "scan" ? <DocumentScannerRounded /> : <UploadFileRounded />}>
                {mode === "scan" ? "Choose receiving document (PDF / image)" : "Choose CSV or Excel file"}
              </Button>
            </label>
            <Typography variant="caption" sx={{ ml: 2, color: "text.secondary" }}>
              {mode === "scan"
                ? "AI reads the document, itemizes it, and groups stock / consumables / gas / PPE / etc."
                : "Columns auto-mapped: name, qty, unit, vendor, PO, category (auto-categorized if absent)."}
            </Typography>
          </Box>

          {busy && <LinearProgress />}
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {items && items.length > 0 && (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {Object.entries(counts).map(([c, n]) => (
                  <Chip key={c} label={`${catLabel(c)}: ${n}`} size="small" color="primary" variant="outlined" />
                ))}
              </Stack>
              <Box sx={{ maxHeight: 420, overflow: "auto", border: "1px solid #eee", borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell><TableCell>Category</TableCell>
                      <TableCell>Qty</TableCell><TableCell>Unit</TableCell>
                      <TableCell>Vendor</TableCell><TableCell>PO</TableCell><TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((it) => (
                      <TableRow key={it._id} hover>
                        <TableCell>
                          <TextField variant="standard" fullWidth value={it.name || ""}
                            onChange={(e) => setField(it._id, "name", e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <Select variant="standard" value={it.category || "other"} sx={{ minWidth: 140 }}
                            onChange={(e) => setField(it._id, "category", e.target.value)}>
                            {INVENTORY_CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                          </Select>
                        </TableCell>
                        <TableCell sx={{ width: 80 }}>
                          <TextField variant="standard" value={it.quantity ?? ""}
                            onChange={(e) => setField(it._id, "quantity", e.target.value)} />
                        </TableCell>
                        <TableCell sx={{ width: 90 }}>
                          <TextField variant="standard" value={it.unit || ""}
                            onChange={(e) => setField(it._id, "unit", e.target.value)} />
                        </TableCell>
                        <TableCell>{it.vendor || "—"}</TableCell>
                        <TableCell>{it.po_number || "—"}</TableCell>
                        <TableCell><IconButton size="small" onClick={() => removeRow(it._id)}><DeleteOutline fontSize="small" /></IconButton></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={commit}
          disabled={busy || !items || items.length === 0}>
          Import {items?.length ? `${items.length} item${items.length > 1 ? "s" : ""}` : ""}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
